import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { CartProvider } from "@/context/CartContext";
import { SiteProvider } from "@/context/SiteContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import About from "@/pages/About";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminLogin from "@/pages/AdminLogin";
import Admin from "@/pages/Admin";
import AdminSite from "@/pages/AdminSite";
import AdminFounding from "@/pages/AdminFounding";
import AdminOrders from "@/pages/AdminOrders";
import AdminShippingSettings from "@/pages/AdminShippingSettings";
import AdminBroadcast from "@/pages/AdminBroadcast";
import AdminReviews from "@/pages/AdminReviews";
import Account from "@/pages/Account";
import AuthCallback from "@/pages/AuthCallback";
import ComingSoon from "@/pages/ComingSoon";
import Contact from "@/pages/Contact";
import Reviews from "@/pages/Reviews";
import Experience from "@/pages/community/Experience";
import { BulletinList, BulletinDetail } from "@/pages/community/Bulletin";
import { EventsList, EventDetail } from "@/pages/community/Events";
import BecomeJenovaGirl from "@/pages/community/BecomeJenovaGirl";
import Prive from "@/pages/community/Prive";
import CommunityWall from "@/pages/community/CommunityWall";
import MyJenova, { TokenPreferences, Guidelines } from "@/pages/community/MyJenova";
import AdminCommunity from "@/pages/AdminCommunity";
import { useSite } from "@/context/SiteContext";
import { useEffect } from "react";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Shell({ children, hideChrome = false }) {
  return (
    <>
      {!hideChrome && <Header />}
      <main>{children}</main>
      {!hideChrome && <Footer />}
    </>
  );
}

function AppRouter() {
  // Detect Emergent Auth callback synchronously (URL fragment #session_id=...)
  const location = useLocation();
  const { site, ready } = useSite();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const isAdmin = typeof window !== "undefined" && !!localStorage.getItem("jenova_admin_token");
  const path = location.pathname;
  const isAdminRoute = path.startsWith("/admin");

  // Paint gate — avoid flashing default site content before DB overrides arrive.
  // Admin routes are exempt so admins can still hit /admin/login without waiting.
  if (!ready && !isAdminRoute) {
    return (
      <div
        data-testid="site-splash"
        className="fixed inset-0 flex items-center justify-center bg-[#F9F9F6]"
      >
        <span className="font-serif text-2xl md:text-3xl tracking-tight text-[#1A1C1A] font-light">
          Jenova <span className="italic font-light text-stone-500">Collective</span>
        </span>
      </div>
    );
  }

  // Prelaunch gating — storefront routes are replaced by ComingSoon.
  // Admins (logged in at /admin/login) bypass the gate so they can test checkout.
  const isStorefront = ["/", "/shop", "/about", "/cart", "/contact", "/reviews"].includes(path) || path.startsWith("/product/") || path.startsWith("/account");
  const inPrelaunch = ready && site.launch_mode === "prelaunch" && !isAdmin;
  if (inPrelaunch && isStorefront) {
    return (
      <Routes>
        <Route path="*" element={<ComingSoon />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Shell><Home /></Shell>} />
      <Route path="/shop" element={<Shell><Shop /></Shell>} />
      <Route path="/product/:id" element={<Shell><ProductDetail /></Shell>} />
      <Route path="/cart" element={<Shell><Cart /></Shell>} />
      <Route path="/about" element={<Shell><About /></Shell>} />
      <Route path="/contact" element={<Shell><Contact /></Shell>} />
      <Route path="/account" element={<Shell><Account /></Shell>} />
      <Route path="/reviews" element={<Shell><Reviews /></Shell>} />
      <Route path="/founding" element={<Navigate to="/shop" replace />} />
      <Route path="/payment/success" element={<Shell><PaymentSuccess /></Shell>} />
      <Route path="/payment/cancel" element={<Shell><PaymentCancel /></Shell>} />
      <Route path="/admin/login" element={<Shell hideChrome><AdminLogin /></Shell>} />
      <Route path="/admin" element={<Shell hideChrome><Admin /></Shell>} />
      <Route path="/admin/site" element={<Shell hideChrome><AdminSite /></Shell>} />
      <Route path="/admin/founding" element={<Shell hideChrome><AdminFounding /></Shell>} />
      <Route path="/admin/orders" element={<Shell hideChrome><AdminOrders /></Shell>} />
      <Route path="/admin/shipping-settings" element={<Shell hideChrome><AdminShippingSettings /></Shell>} />
      <Route path="/admin/broadcast" element={<Shell hideChrome><AdminBroadcast /></Shell>} />
      <Route path="/admin/reviews" element={<Shell hideChrome><AdminReviews /></Shell>} />
      <Route path="/admin/community" element={<Shell hideChrome><AdminCommunity /></Shell>} />
      <Route path="/experience" element={<Shell><Experience /></Shell>} />
      <Route path="/bulletin" element={<Shell><BulletinList /></Shell>} />
      <Route path="/bulletin/:id" element={<Shell><BulletinDetail /></Shell>} />
      <Route path="/events" element={<Shell><EventsList /></Shell>} />
      <Route path="/events/:id" element={<Shell><EventDetail /></Shell>} />
      <Route path="/become-a-jenova-girl" element={<Shell><BecomeJenovaGirl /></Shell>} />
      <Route path="/prive" element={<Shell><Prive /></Shell>} />
      <Route path="/community-wall" element={<Shell><CommunityWall /></Shell>} />
      <Route path="/my-jenova" element={<Shell><MyJenova /></Shell>} />
      <Route path="/sisters/*" element={<Navigate to="/experience" replace />} />
      <Route path="/community/preferences" element={<Shell><TokenPreferences /></Shell>} />
      <Route path="/community/guidelines" element={<Shell><Guidelines /></Shell>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#F9F9F6]">
      <BrowserRouter>
        <SiteProvider>
          <CustomerAuthProvider>
            <CartProvider>
              <ScrollToTop />
              <Toaster position="top-center" richColors />
              <AppRouter />
            </CartProvider>
          </CustomerAuthProvider>
        </SiteProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
